import Image from "next/image";
import React from "react";
import Utils from "../../utils/Utils";

export const StatsExplained = ({ show = true }) => {
  return (
    <div className="container stats-explained">
      <div className="text-left">
        <h2>Calculation</h2>
        <p>
          <b>How exactly do we calculate ShotQuality?</b>
        </p>
      </div>
      <div className="text-left">
        <p>
          <b style={{ color: "#d07c3b" }}>Shot Quality</b> predicts shot outcomes by evaluating the quality of each
          attempt. Each shot is graded on a 0-100 percent scale, which represents the likelihood the shot results in a
          made basket, and then is multiplied by the amount of points attempted to get the ShotQuality value.
        </p>

        <h5>Example</h5>

        <p>
          We differentiate our model from others through the incorporation of individual metrics, because expectations
          should be different between Steph Curry and Dwight Howard when they take a long three point attempt.{" "}
        </p>

        <p>
          For example, Chris Paul has an excellent midrange shot, he makes 55 percent of his elbow jumpers for a
          ShotQuality EV of 1.10. Russell Westbrook, on the other hand, makes 35 percent of his elbow jumpers for a
          ShotQuality value of 0.70.
        </p>

        <h5>Threes vs Twos</h5>

        <p>
          The type of shot (2 pointer or 3 pointer) is always known, but calculating the percent chance the shot has of
          going in is the challenging part. Both of these pieces of information are important context when
          differentiating between all types of shots.
        </p>

        <p>So then, Shot Quality = 3 (type of shot) * .25 (% chance of going in) = 0.75 ShotQuality Value</p>

        <p>
          Or if there is a mid range shot (2 pointer) that has a 35% change of going in . Then: Shot Quality= 2 (type of
          shot) * .35 (% chance of going in) = 0.70 ShotQuality value
        </p>

        <div className="Aboutside">
          <div className="container">
            <h5>Model</h5>
            <p>
              Over 90 variables are incorporated into the ShotQuality value for each shot. This level of granularity
              allows us to evaluate shot making and luck. The most important aspect in calculating ShotQuality is
              computing the individual player's shot making ability, and there are a plethora of factors involved in
              quantifying this. Here are a list of the variables
            </p>
            <div className="row">
              <div className="col-md-4">
                <ul>
                  <li>Player's 3PT shooting ability</li>
                  <li>Player's catch and shoot vs. off-the-dribble shooting</li>
                  <li>Player's deep midrange shooting ability</li>
                  <li>Player's short midrange shooting ability</li>
                  <li>Player's driving ability</li>
                  <li>Player's cutting ability (high-post, drop-off pass, or from screen)</li>
                </ul>
              </div>
              <div className="col-md-4">
                <ul>
                  <li>Player's isolation ability</li>
                  <li>Player's ability curling off a screen for a jump shot</li>
                  <li>
                    Player's FT%
                    <ol>
                      <li>Single Bonus vs. Double Bonus</li>
                    </ol>
                  </li>
                  <li>Player's free throw rate</li>
                  <li>Player's turnover rate</li>
                  <li>Shot Distance</li>
                </ul>
              </div>
              <div className="col-md-4">
                <ul>
                  <li>Defender Closeout</li>
                  <li>Interior Defense</li>
                  <li>
                    Blocked Shots
                    <ol>
                      <li>Incorporating the likelihood of an offensive rebound or foul drawn</li>
                    </ol>
                  </li>
                  <li>Elimination of Half-Court Shots</li>
                  <li>Percent chance of Offensive Rebound based on shot type and shot distance</li>
                </ul>
              </div>
            </div>

            <p className="mt-5">
              While sample sizes matter, we are constantly looking for ways to maximize predictability and maintain good
              calibration. As you can see in the charts below, the more shots an individual player takes (roughly after
              only a game or two) the more calibrated their ShotQuality values are.
            </p>

            <Image
              src={Utils.getAsset("stats-explained-calculation.png")}
              alt="expectations vs actual performance"
              className="rounded"
            />
          </div>
        </div>
        <p></p>
      </div>
    </div>
  );
};
